/*
 * Recommended products of a bundle offer (snippets/product-bundle-offers.liquid) follow the size
 * chosen for the main product: a protector switches to the mattress size, shows its price and
 * "+" adds that size straight to the cart. Without the same size (or when it is sold out) the card
 * stays as it is and "+" opens the theme's size picker.
 */
(() => {
  if (customElements.get('bundle-offer-sizes')) return;

  // Size option names across the stores (BG, GR/CY, RO, RS), compared trimmed and lowercased.
  const SIZE_OPTION_NAMES = [
    'size',
    'размер',
    'размери',
    'за размер матрак',
    'размер на матрак',
    'μέγεθος',
    'διαστάσεις',
    'για μέγεθος στρώματος',
    'dimensiuni',
    'dimensiune',
    'dimensiuni pat',
    'dimensiuni saltea',
    'mărime',
    'by mattress size',
    'dimenzije',
    'dimenzija',
    'veličina',
    'za dimenziju dušeka',
  ];

  // "160/200", "160x200", "160х200" (Cyrillic х) -> ["160x200"]
  // "160x190/200" (protectors that fit both lengths) -> ["160x190", "160x200"]
  const canonicalSizes = (value) => {
    const clean = String(value ?? '')
      .toLowerCase()
      .replace(/[х×]/g, 'x')
      .replace(/\s|см|cm/g, '');
    const match = clean.match(/^(\d+)[x/](\d+)(?:\/(\d+))?$/);
    if (!match) return [clean];
    const [, width, length, altLength] = match;
    return altLength ? [`${width}x${length}`, `${width}x${altLength}`] : [`${width}x${length}`];
  };

  const sizeOptionIndex = (names) =>
    names.findIndex((name) => SIZE_OPTION_NAMES.includes(String(name).trim().toLowerCase()));

  const parseJson = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  class BundleOfferSizes extends HTMLElement {
    connectedCallback() {
      this.mainSizeIndex = sizeOptionIndex(parseJson(this.dataset.mainOptions, []));
      if (this.mainSizeIndex === -1) return;

      this.cards = Array.from(this.querySelectorAll('[data-offer-variants]'))
        .map((script) => this.setupCard(script))
        .filter(Boolean);
      if (!this.cards.length) return;

      this.onVariantChange = (event) => this.follow(event.detail?.variant?.options);
      this.productContainer = this.closest('[data-section-type], [data-product-template], .product-single') || document;
      this.productContainer.addEventListener('theme:variant:change', this.onVariantChange);

      const initial = parseJson(this.dataset.mainVariant, null);
      if (initial) this.follow(initial);
    }

    disconnectedCallback() {
      this.productContainer?.removeEventListener('theme:variant:change', this.onVariantChange);
    }

    setupCard(script) {
      const data = parseJson(script.textContent, null);
      const card = script.previousElementSibling;
      const input = card?.querySelector('[data-upsell-size-variant]');
      const button = card?.querySelector('[data-quick-add-modal-handle]');
      const sizeIndex = data ? sizeOptionIndex(data.options) : -1;
      if (!input || !button || sizeIndex === -1) return null;

      const price = card.querySelector('.product-upsell__price');
      const state = {
        card,
        input,
        button,
        sizeIndex,
        variants: data.variants,
        price,
        priceHtml: price?.innerHTML ?? '',
        offerPrice: card.querySelector('.product-bundle-offer__price'),
        label: null,
      };
      state.offerHtml = state.offerPrice?.innerHTML ?? '';

      // Runs before the theme's own click handler on the button, which opens the size picker.
      card.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('[data-quick-add-modal-handle]') || input.disabled) return;
          event.preventDefault();
          event.stopPropagation();
          window.a11y && (window.a11y.lastElement = button);
          document.dispatchEvent(new CustomEvent('theme:cart:add', {detail: {button}}));
        },
        true,
      );

      return state;
    }

    follow(mainOptions) {
      const sizes = mainOptions ? canonicalSizes(mainOptions[this.mainSizeIndex]) : [];
      this.cards.forEach((state) => {
        const variant = sizes.length
          ? state.variants.find(
              (item) => item.available && canonicalSizes(item.options[state.sizeIndex]).some((size) => sizes.includes(size)),
            )
          : null;
        this.show(state, variant);
      });
    }

    show(state, variant) {
      state.input.disabled = !variant;
      state.input.value = variant ? variant.id : '';

      if (state.price) {
        state.price.classList.toggle('sale', Boolean(variant?.compare));
        state.price.innerHTML = variant
          ? `<span class="new-price">${variant.price}</span>${variant.compare ? ` <span class="old-price">${variant.compare}</span>` : ''}`
          : state.priceHtml;
      }
      if (state.offerPrice) state.offerPrice.innerHTML = variant ? variant.offer : state.offerHtml;

      if (variant && !state.label) {
        state.label = document.createElement('p');
        state.label.className = 'product-upsell__variant-title h4';
        state.price?.before(state.label);
      }
      if (state.label) {
        state.label.hidden = !variant;
        state.label.textContent = variant ? variant.options[state.sizeIndex] : '';
      }
    }
  }

  customElements.define('bundle-offer-sizes', BundleOfferSizes);
})();
